import { useEffect } from 'react';
import AuthUtil from '../core/helpers/AuthUtil';
import { RolesEnum } from '../constatns';

const CHECK_INTERVAL_MS = 60 * 1000;
const IDLE_RELOAD_MS = 2 * 60 * 1000;
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll', 'wheel'];

/**
 * Причины перезагрузок складываем в localStorage (последние 20) по образцу
 * authRedirectLog из axios.ts: по жалобе "у меня сама обновилась страница"
 * иначе не отличить наш деплой от того, что Chrome выгрузил вкладку
 * (Memory Saver) или пользователь попал на под со старой сборкой.
 */
const RELOAD_LOG_KEY = 'autoReloadLog';

function logEvent(event: string, details?: Record<string, unknown>) {
    try {
        const prev = JSON.parse(localStorage.getItem(RELOAD_LOG_KEY) || '[]');
        const log = Array.isArray(prev) ? prev : [];
        log.push({ event, at: new Date().toISOString(), ...details });
        localStorage.setItem(RELOAD_LOG_KEY, JSON.stringify(log.slice(-20)));
    } catch {
        // логирование не должно мешать самому обновлению
    }
}

async function fetchMainJsHash(): Promise<string | null> {
    try {
        const res = await fetch('/asset-manifest.json', { cache: 'no-store' });
        if (!res.ok) return null;
        const data = await res.json();
        return data?.files?.['main.js'] ?? null;
    } catch {
        return null;
    }
}

/**
 * Казначею страницу автоматически не перезагружаем: у него длинные операции
 * с курсами и заявками, которые нельзя обрывать перезагрузкой. Роль проверяем
 * динамически (из localStorage), а не один раз при монтировании, потому что
 * компонент живёт всё время работы вкладки, в том числе до и после логина.
 */
function isAutoUpdateBlockedRole(): boolean {
    try {
        return AuthUtil.hasRole(RolesEnum.Treasurer);
    } catch {
        return false;
    }
}

function currentRoles(): string[] {
    try {
        return AuthUtil.getRoles().map((r) => r.code);
    } catch {
        return [];
    }
}

/**
 * Молча перезагружает страницу, когда на сервере вышла новая версия сборки,
 * но только в безопасный момент - пока вкладка свёрнута/не в фокусе,
 * либо после нескольких минут бездействия пользователя. Так деплой
 * не обрывает заполнение формы или кассовую операцию на середине.
 */
export default function AutoUpdateOnIdle() {
    useEffect(() => {
        if (process.env.NODE_ENV !== 'production') return;

        let cancelled = false;
        let baseline: string | null = null;
        let pendingReload = false;
        let idleTimer: ReturnType<typeof setTimeout> | null = null;

        // Кандидат на новую версию: во время rolling update балансировщик
        // отдаёт то старый под, то новый, и хеш "мигает". Перезагружаемся
        // только когда один и тот же новый хеш пришёл дважды подряд, иначе
        // вкладка перезагружается по кругу и может приземлиться на старый под.
        let candidate: string | null = null;
        let candidateHits = 0;

        const navEntry = performance.getEntriesByType('navigation')[0] as
            | PerformanceNavigationTiming
            | undefined;

        logEvent('mount', {
            navigationType: navEntry?.type,
            // Chrome помечает так вкладку, которую сам выгрузил из памяти
            // и восстановил при возврате - это не наша перезагрузка.
            wasDiscarded: (document as Document & { wasDiscarded?: boolean }).wasDiscarded === true,
            roles: currentRoles()
        });

        const reload = (reason: string) => {
            if (cancelled || isAutoUpdateBlockedRole()) return;
            logEvent('reload', { reason, from: baseline, to: candidate, roles: currentRoles() });
            window.location.reload();
        };

        const armIdleTimer = () => {
            if (idleTimer) clearTimeout(idleTimer);
            if (!pendingReload) return;
            idleTimer = setTimeout(() => {
                if (pendingReload) reload('простой пользователя');
            }, IDLE_RELOAD_MS);
        };

        const onActivity = () => {
            if (pendingReload) armIdleTimer();
        };

        const check = async () => {
            if (isAutoUpdateBlockedRole()) return;

            const hash = await fetchMainJsHash();
            if (cancelled || !hash) return;

            if (baseline === null) {
                baseline = hash;
                logEvent('baseline', { hash });
                return;
            }

            if (hash === baseline) {
                // хеш вернулся к исходному - значит это было мигание выката,
                // а не новая версия: снимаем готовность перезагружаться
                if (pendingReload || candidate) {
                    logEvent('flap', { baseline, candidate });
                    pendingReload = false;
                    candidate = null;
                    candidateHits = 0;
                    if (idleTimer) clearTimeout(idleTimer);
                }
                return;
            }

            if (pendingReload) return;

            if (hash !== candidate) {
                candidate = hash;
                candidateHits = 1;
                return;
            }

            candidateHits += 1;
            if (candidateHits < 2) return;

            pendingReload = true;
            logEvent('new-version', { baseline, candidate });

            if (document.visibilityState === 'hidden') {
                reload('вкладка не активна');
            } else {
                armIdleTimer();
            }
        };

        const onVisibilityChange = () => {
            if (document.visibilityState === 'hidden' && pendingReload) {
                reload('вкладка ушла в фон');
                return;
            }
            if (document.visibilityState === 'visible') {
                check();
                armIdleTimer();
            }
        };

        check();
        const intervalId = setInterval(check, CHECK_INTERVAL_MS);
        document.addEventListener('visibilitychange', onVisibilityChange);
        ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, onActivity, { passive: true }));

        return () => {
            cancelled = true;
            clearInterval(intervalId);
            if (idleTimer) clearTimeout(idleTimer);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, onActivity));
        };
    }, []);

    return null;
}
